/**
 * §P.7 · El personal del club.
 *
 * ## Es la pantalla que puede dejar a un club fuera de su propio panel
 *
 * Por eso el contrato tiene dos puertas cerradas —`409 last_owner_standing` y `409
 * cannot_change_own_role`— y por eso aquí no se enseñan como errores: **las dos
 * cosas que las disparan no se ofrecen**. Un botón que sabes que va a fallar es un
 * botón que no debería estar; el 409 sigue existiendo para quien llame a la API
 * directamente, y para la carrera de dos dueños que se degradan a la vez.
 *
 * Y una tercera que el contrato **no** cierra: la pantalla tampoco deja sacarse uno
 * a sí mismo del club. Eso el servidor lo permite, y con un segundo dueño en pie es
 * legítimo —«me voy»—. Pero es la única acción de aquí que no tiene deshacer desde
 * el panel: quien se saca pierde la pantalla donde volver a meterse. Un dueño que
 * de verdad se va tiene salida —asciende a otro y ese le saca—, y a cambio no hay
 * forma de cerrarse la puerta por detrás con un clic.
 *
 * ## `invitation_sent` cambia la frase, no el resultado
 *
 * Son tres casos y solo uno manda correo: cuenta nueva, cuenta que existe pero no
 * es de este club, y persona que ya estaba dentro. Decirle «te han invitado a
 * Dojang Pascual» a quien lleva dos años usándolo es cómo un producto parece roto,
 * así que la pantalla lee ese booleano y dice la verdad de lo que pasó.
 *
 * Y la verdad del tercer caso es que **no pasó nada**: `InviteMemberToClub` ve la
 * membresía activa y sale sin escribir, así que el rol que se pidió se ignora. Lo
 * primero que puse aquí decía «le hemos puesto el rol nuevo», que era mentira y se
 * vio pidiendo `admin` para un `owner` — volvió `owner`. Un mensaje de éxito que
 * afirma un cambio que no ocurrió es peor que no decir nada.
 *
 * ## `venues` vacío significa **todas** las sedes
 *
 * No ninguna. El club de una sola sede no tiene filas ahí, y leerlo al revés
 * dejaría a todo el personal aparentemente sin acceso a nada.
 *
 * ## Y limitar por sede solo aparece si hay más de una · §P.7 fase 3, 22-09-2026
 *
 * `PUT /memberships/{id}/venues` existe desde el principio en el esquema y no tenía
 * pantalla. Sin ella, **un entrenador de la sede B ve los alumnos de la A**.
 *
 * El control no se enseña en un club de una sola sede, y no es por ahorrar sitio: la
 * única acción posible allí sería marcar la única sede que hay, que no limita nada, o
 * desmarcarla, que —por la regla de arriba— tampoco. Un control cuyas dos posiciones
 * significan lo mismo no es un control, es una trampa.
 *
 * ## Suspender no es revocar
 *
 * Suspender apaga el acceso y deja la persona y su historial; revocar la saca del
 * club. Lo normal es lo primero —un entrenador que se va una temporada—, así que es
 * lo que está a mano y lo otro va detrás de una pregunta.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import { TextoConEnfasis } from '@/shared/ui/TextoConEnfasis';
import { fecha } from '@/shared/ui/formato';
import {
  cuantoQuedaDeDocumento,
  estadoDeDocumento,
  etiquetaDeClaseDeDocumento,
  etiquetaDeEstadoDePersonal,
  etiquetaDeRol,
  ROLES_DE_PERSONAL,
  type DocumentoDePersonal,
  type MiembroDelPersonal,
  type ResultadoDeInvitacion,
  type RolDePersonal,
  type Sede,
} from '@/club/ajustes/tipos';

/** Cómo salió la última invitación. Los dos casos se cuentan distinto. */
interface Invitado {
  readonly nombre: string;
  readonly correoMandado: boolean;
}

export function Personal({
  club,
  yoSoy,
  alVerPapeles,
  conRoles = true,
  conSedes = true,
}: {
  club: string;
  yoSoy: string;
  /**
   * Si el plan incluye administrativos y ayudantes (`roles`, §A.2.1). Sin él se
   * invitan solo entrenadores; a quien ya lo es no se le quita el rol, y por eso el
   * suyo sigue en su desplegable.
   */
  conRoles?: boolean;
  /** Si el plan incluye limitar entrenadores por sede (`multisede`). */
  conSedes?: boolean;
  /** Abre los papeles de una persona. Ver `DocumentosDePersonal`. */
  alVerPapeles: (usuarioId: string, nombre: string) => void;
}) {
  const cache = useQueryClient();
  const [invitando, setInvitando] = useState(false);
  const [correo, setCorreo] = useState('');
  const [nombre, setNombre] = useState('');
  const [rol, setRol] = useState<RolDePersonal>('coach');
  const [invitado, setInvitado] = useState<Invitado | null>(null);
  const [revocando, setRevocando] = useState<MiembroDelPersonal | null>(null);
  /** La membresía a la que se le están tocando las sedes. */
  const [limitando, setLimitando] = useState<string | null>(null);
  const [problema, setProblema] = useState<Problema | null>(null);

  const personal = useQuery({
    queryKey: ['personal', club],
    queryFn: async () => {
      const { data, error } = await api.v1.clubs({ club }).memberships.get({
        query: {},
      });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as MiembroDelPersonal[];
    },
  });

  /*
   * Las sedes, para poder limitar a quién trabaja dónde. Se piden siempre porque de
   * ellas depende **si el control se enseña**: con una sola sede no hay nada que
   * limitar, y el propio recuento es la respuesta.
   */
  const sedes = useQuery({
    queryKey: ['sedes', club],
    queryFn: async () => {
      const { data, error } = await api.v1
        .clubs({ club })
        .venues.get({ query: {} });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as Sede[];
    },
  });

  const limitar = useMutation({
    mutationFn: async ({
      id,
      venue_ids,
    }: {
      id: string;
      venue_ids: string[];
    }) => {
      const r = await api.v1
        .clubs({ club })
        .memberships({ membershipId: id })
        .venues.put({ venue_ids });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      setProblema(null);
      void cache.invalidateQueries({ queryKey: ['personal', club] });
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  /*
   * Los papeles que caducan pronto, **de todo el club**.
   *
   * Es la parte accionable de §P.7 fase 2, y va aquí y no en una pestaña propia por lo
   * mismo que `consents/missing` abre la sección de cumplimiento: una lista que hay
   * que perseguir no se entierra donde nadie pasa. El dueño ya viene a esta pantalla.
   */
  const caducan = useQuery({
    queryKey: ['documentos-que-caducan', club],
    queryFn: async () => {
      const { data, error } = await api.v1
        .clubs({ club })
        .staff.documents.expiring.get({ query: {} });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as DocumentoDePersonal[];
    },
  });

  const invitar = useMutation({
    mutationFn: async () => {
      const r = await api.v1.clubs({ club }).memberships.post({
        email: correo.trim(),
        role: rol,
        first_name: nombre.trim() === '' ? null : nombre.trim(),
      });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data as unknown as ResultadoDeInvitacion;
    },
    onSuccess: (r) => {
      setInvitando(false);
      setCorreo('');
      setNombre('');
      setInvitado({
        nombre: r.member.name || r.member.email,
        correoMandado: r.invitation_sent,
      });
      void cache.invalidateQueries({ queryKey: ['personal', club] });
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  const cambiar = useMutation({
    mutationFn: async (v: {
      id: string;
      role?: RolDePersonal;
      status?: 'active' | 'suspended';
    }) => {
      const r = await api.v1
        .clubs({ club })
        .memberships({ membershipId: v.id })
        .patch({
          ...(v.role ? { role: v.role } : {}),
          ...(v.status ? { status: v.status } : {}),
        });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      setProblema(null);
      // El acuse de la invitación se refiere a la acción anterior: dejarlo puesto
      // mientras se cambian roles lo convierte en un cartel que ya no dice nada.
      setInvitado(null);
      void cache.invalidateQueries({ queryKey: ['personal', club] });
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  const revocar = useMutation({
    mutationFn: async (id: string) => {
      const r = await api.v1
        .clubs({ club })
        .memberships({ membershipId: id })
        .delete();
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      setRevocando(null);
      setInvitado(null);
      void cache.invalidateQueries({ queryKey: ['personal', club] });
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  // ── Revocar ──────────────────────────────────────────────────────────────
  if (revocando) {
    return (
      <div className="hueco hueco-confirmar">
        <h3>¿Sacar a {revocando.name || revocando.email} del club?</h3>

        <ul className="lista-clara">
          <li>
            Pierde el acceso al panel. Lo que hizo —las listas que pasó, los
            grados que puso— <strong>se queda</strong>: el historial del club no
            se toca.
          </li>
          <li>
            Si solo se va una temporada, <strong>suspéndele</strong> en vez de
            esto: vuelve con un clic y sin reinvitar.
          </li>
        </ul>

        {problema && <PanelError problema={problema} />}

        <div className="hueco-acciones">
          <button
            type="button"
            className="btn-peligro"
            disabled={revocar.isPending}
            onClick={() => revocar.mutate(revocando.membership_id)}
          >
            {revocar.isPending ? 'Sacando…' : 'Sí, sacarle'}
          </button>
          <button
            type="button"
            className="btn-plano"
            onClick={() => {
              setRevocando(null);
              setProblema(null);
            }}
          >
            Dejarlo
          </button>
        </div>
      </div>
    );
  }

  // ── Invitar ──────────────────────────────────────────────────────────────
  if (invitando) {
    return (
      <form
        className="formulario"
        onSubmit={(e) => {
          e.preventDefault();
          setProblema(null);
          invitar.mutate();
        }}
      >
        <h1>Invitar a alguien</h1>

        <div className="rejilla">
          <label>
            Correo
            <input
              required
              type="email"
              maxLength={200}
              placeholder="entrenador@ejemplo.es"
              value={correo}
              onChange={(e) => setCorreo(e.target.value)}
            />
          </label>

          <label>
            Nombre
            <input
              maxLength={80}
              placeholder="Marta"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
            />
          </label>
        </div>

        <h2 className="seccion">Qué va a poder hacer</h2>

        {/*
          Los tres roles con lo que significan de verdad, no solo su nombre: «coach»
          no le dice a nadie que ese rol no ve el dinero, y esa es justo la decisión
          que se está tomando aquí.
        */}
        <ul className="roles">
          {ROLES_DE_PERSONAL.filter((r) => conRoles || r.valor === 'coach').map(
            (r) => (
              <li key={r.valor}>
                <label className="opcion">
                  <input
                    type="radio"
                    name="rol"
                    checked={rol === r.valor}
                    onChange={() => setRol(r.valor)}
                  />
                  <span>
                    <strong>{r.etiqueta}</strong>
                    {/*
                    `strong` + `em` es el patrón de casa para una opción con
                    explicación, y `TextoConEnfasis` porque estas frases llevan
                    negrita ligera: lo que decide el club es «**no ve el dinero de
                    nadie**», no el nombre del rol.
                  */}
                    <em>
                      <TextoConEnfasis texto={r.explicacion} />
                    </em>
                  </span>
                </label>
              </li>
            )
          )}
        </ul>

        <p className="tenue">
          Al dueño no se le invita: se le asciende desde la lista, y siempre
          tiene que quedar uno.
        </p>

        {problema && <PanelError problema={problema} />}

        <div className="acciones">
          <button type="submit" disabled={invitar.isPending}>
            {invitar.isPending ? 'Invitando…' : 'Mandar la invitación'}
          </button>
          <button
            type="button"
            className="btn-plano"
            disabled={invitar.isPending}
            onClick={() => {
              setInvitando(false);
              setProblema(null);
            }}
          >
            Cancelar
          </button>
        </div>
      </form>
    );
  }

  // ── La lista ─────────────────────────────────────────────────────────────
  if (personal.isPending) {
    return <Cargando filas={4} etiqueta="Cargando el personal" />;
  }

  if (personal.isError || !personal.data) {
    return (
      <PanelError
        problema={aProblema(personal.error)}
        reintentar={() => void personal.refetch()}
      />
    );
  }

  // El último dueño activo no se toca: ni se le cambia el rol ni se le suspende.
  const duenosActivos = personal.data.filter(
    (m) => m.role === 'owner' && m.status === 'active'
  ).length;

  return (
    <section>
      <header className="titulo-fila">
        <h1>Personal</h1>
        <button
          type="button"
          onClick={() => {
            setInvitando(true);
            setInvitado(null);
            setProblema(null);
          }}
        >
          Invitar a alguien
        </button>
      </header>

      <p className="tenue">
        Quién entra en el panel del club y hasta dónde llega. Los tutores no van
        aquí: a un tutor se le invita desde la ficha de su alumno.
      </p>

      {/*
        El aviso de los papeles, arriba y solo cuando hay algo. El certificado de
        delitos sexuales es obligatorio por ley y el club responde de tenerlo al día:
        enterarse de que venció por una inspección es el peor momento posible.
      */}
      {caducan.data && caducan.data.length > 0 && (
        <div
          className={
            caducan.data.some((d) => estadoDeDocumento(d) === 'caducado')
              ? 'aviso aviso-grave'
              : 'aviso'
          }
          role="alert"
        >
          <strong>
            {caducan.data.length === 1
              ? 'Hay un papel que caduca o ha caducado.'
              : `Hay ${caducan.data.length} papeles que caducan o han caducado.`}
          </strong>
          <ul className="lista-clara">
            {caducan.data.map((d) => (
              <li key={d.id}>
                {etiquetaDeClaseDeDocumento(d.kind)} de{' '}
                <strong>{d.name ?? 'alguien del personal'}</strong>:{' '}
                {cuantoQuedaDeDocumento(d.days_to_expiry).toLowerCase()}.
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Dos frases distintas, porque pasaron dos cosas distintas. */}
      {invitado && (
        <div className="aviso aviso-calma" role="status">
          {invitado.correoMandado ? (
            <>
              <strong>Invitación mandada a {invitado.nombre}.</strong> Entrará
              en cuanto abra el correo y acepte.
            </>
          ) : (
            <>
              <strong>{invitado.nombre} ya está en el club.</strong> No hemos
              hecho nada: ni correo ni cambio de rol. Si quieres cambiárselo,
              usa el desplegable de su fila.
            </>
          )}
        </div>
      )}

      {problema && <PanelError problema={problema} />}

      {personal.data.length === 0 ? (
        <Vacio
          titulo="No hay nadie más en el club"
          explicacion="Invita a un entrenador para que pase lista y ponga grados, o a un administrativo para que lleve los cobros."
          accion={
            <button type="button" onClick={() => setInvitando(true)}>
              Invitar al primero
            </button>
          }
        />
      ) : (
        <ul className="personal">
          {personal.data.map((m) => {
            const soyYo = m.user_id === yoSoy;
            const ultimoDueno =
              m.role === 'owner' &&
              m.status === 'active' &&
              duenosActivos === 1;
            // Ni el último dueño en pie, ni uno a sí mismo. Ver la cabecera.
            const intocable = soyYo || ultimoDueno;

            return (
              <li
                key={m.membership_id}
                className={m.status === 'active' ? undefined : 'fila-fuera'}
              >
                <div className="aviso-cabeza">
                  <strong>{m.name || m.email}</strong>
                  <span className="pastilla pastilla-info">
                    {etiquetaDeRol(m.role)}
                  </span>
                  {m.status !== 'active' && (
                    <span
                      className={`pastilla ${
                        m.status === 'suspended'
                          ? 'pastilla-mal'
                          : 'pastilla-paused'
                      }`}
                    >
                      {etiquetaDeEstadoDePersonal(m.status)}
                    </span>
                  )}
                  {soyYo && <span className="pastilla">Eres tú</span>}
                </div>

                <p className="tenue">
                  {m.email}
                  {/* Vacío es **todas** las sedes, y decirlo evita leerlo al revés. */}
                  {m.venues.length === 0
                    ? ' · todas las sedes'
                    : ` · ${m.venues.map((v) => v.name).join(', ')}`}
                  {m.accepted_at
                    ? ` · dentro desde ${fecha(m.accepted_at)}`
                    : m.invited_at
                      ? ` · invitado el ${fecha(m.invited_at)}`
                      : ''}
                </p>

                {ultimoDueno && (
                  <p className="tenue">
                    Es el único dueño en pie: mientras lo sea, no se le puede
                    cambiar el rol ni suspender. Asciende a otra persona antes.
                  </p>
                )}

                <div className="aviso-pie">
                  <span className="tutor-acciones" style={{ margin: 0 }}>
                    <label className="rol-en-linea">
                      Rol
                      <select
                        value={m.role}
                        disabled={intocable || cambiar.isPending}
                        onChange={(e) =>
                          cambiar.mutate({
                            id: m.membership_id,
                            role: e.target.value as RolDePersonal,
                          })
                        }
                      >
                        {/* `owner` solo como opción de ascenso, nunca de alta. */}
                        <option value="owner">Dueño</option>
                        {ROLES_DE_PERSONAL.filter(
                          (r) =>
                            conRoles ||
                            r.valor === 'coach' ||
                            r.valor === m.role
                        ).map((r) => (
                          <option key={r.valor} value={r.valor}>
                            {r.etiqueta}
                          </option>
                        ))}
                      </select>
                    </label>

                    {m.status === 'suspended' ? (
                      <button
                        type="button"
                        className="btn-plano"
                        disabled={cambiar.isPending}
                        onClick={() =>
                          cambiar.mutate({
                            id: m.membership_id,
                            status: 'active',
                          })
                        }
                      >
                        Reactivar
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn-plano"
                        disabled={intocable || cambiar.isPending}
                        onClick={() =>
                          cambiar.mutate({
                            id: m.membership_id,
                            status: 'suspended',
                          })
                        }
                      >
                        Suspender
                      </button>
                    )}

                    {/*
                      Los papeles **no** están detrás de `intocable`: el dueño tiene
                      que poder anotar su propio certificado, y es justo el que más
                      falta suele hacer porque nadie se lo pide a sí mismo.
                    */}
                    <button
                      type="button"
                      className="btn-plano"
                      onClick={() => alVerPapeles(m.user_id, m.name || m.email)}
                    >
                      Papeles
                    </button>

                    {/*
                      Solo con más de una sede. Ver la cabecera: en un club de una
                      sola, las dos posiciones del control significan lo mismo.
                    */}
                    {conSedes && (sedes.data?.length ?? 0) > 1 && (
                      <button
                        type="button"
                        className="btn-plano"
                        onClick={() =>
                          setLimitando(
                            limitando === m.membership_id
                              ? null
                              : m.membership_id
                          )
                        }
                      >
                        Sedes
                      </button>
                    )}

                    <button
                      type="button"
                      className="btn-plano"
                      disabled={intocable}
                      onClick={() => {
                        setRevocando(m);
                        setProblema(null);
                      }}
                    >
                      Sacar del club
                    </button>
                  </span>
                </div>

                {limitando === m.membership_id && sedes.data && (
                  <div className="hueco hueco-confirmar">
                    <h3>¿En qué sedes trabaja?</h3>
                    <p>
                      Solo verá los alumnos y las clases de las sedes marcadas.{' '}
                      <strong>Sin marcar ninguna, las ve todas</strong> — que es
                      lo normal y lo que tienen ahora casi todos.
                    </p>

                    <div className="rejilla">
                      {sedes.data.map((sede) => {
                        const marcada = m.venues.some((v) => v.id === sede.id);
                        return (
                          <label key={sede.id} className="opcion">
                            <input
                              type="checkbox"
                              checked={marcada}
                              disabled={limitar.isPending}
                              onChange={() =>
                                limitar.mutate({
                                  id: m.membership_id,
                                  venue_ids: marcada
                                    ? m.venues
                                        .filter((v) => v.id !== sede.id)
                                        .map((v) => v.id)
                                    : [...m.venues.map((v) => v.id), sede.id],
                                })
                              }
                            />
                            {sede.name}
                          </label>
                        );
                      })}
                    </div>

                    <div className="hueco-acciones">
                      <button
                        type="button"
                        className="btn-plano"
                        onClick={() => setLimitando(null)}
                      >
                        Listo
                      </button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
